import {
  Grid,
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Text,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Alert,
  AlertIcon,
  useToast
} from '@chakra-ui/react'
import { drawDOM, exportPDF } from '@progress/kendo-drawing'
import { format } from 'date-fns'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { FiDownload, FiRefreshCw, FiFileText } from 'react-icons/fi'

import usePurchaseSchedule from '@hook/dashboard/use-purchase-schedule'
import useFilters from '@hook/shared/use-filters'
import useLayout from '@hook/shared/use-layout'
import useSessions from '@hook/shared/use-sessions'

import CalendarView from '@component/Dashboard/PurchaseSchedule/CalendarView'
import DataTable from '@component/Dashboard/PurchaseSchedule/DataTable'
import HistoricoTable from '@component/Dashboard/PurchaseSchedule/HistoricoTable'
import InsightsSection from '@component/Dashboard/PurchaseSchedule/InsightsSection'
import StatusCharts from '@component/Dashboard/PurchaseSchedule/StatusChart'
import Layout from '@component/Layout'
import LayoutFilters from '@component/Layout/Filters'
import LayoutLoader from '@component/Layout/Loader'

import { getVisualizaTodosCompradores } from '@helper/auth/getUserTokenClaims'

const HISTORICO_TAB = 1

const PurchaseScheduleDashboard: FC = () => {
  const { menuToggle } = useLayout()
  const { getFilters } = useFilters()
  const toast = useToast()
  // Área exportada no PDF: apenas o resumo (gráficos + insights)
  const summaryRef = useRef<HTMLDivElement>(null)
  const [loadingPDF, setLoadingPDF] = useState(false)
  const [tabIndex, setTabIndex] = useState(0)

  const { hasSessions } = useSessions()
  const {
    loading,
    loadWarning,
    getData,
    cancel,
    exportData,
    statusCounts,
    filters,
    setFilters,
    params,
    filtersList,
    handleUpdateParams,
    clearAllFilters,
    historico,
    loadingHistorico,
    getHistorico
  } = usePurchaseSchedule()

  // O provider já inicia params com o período padrão (últimos 30 dias)
  const periodoFiltro = useMemo(
    () => ({
      initialDate: params.initialDate || '',
      finalDate: params.finalDate || ''
    }),
    [params.initialDate, params.finalDate]
  )

  const empresasIds: number[] = useMemo(
    () =>
      (params.companies || [])
        .map((item: any) => Number(item?.value))
        .filter((id: number) => id > 0),
    [params.companies]
  )
  const hasEmpresa = empresasIds.length > 0
  const empresasKey = empresasIds.join(',')

  const height = useMemo(
    () =>
      menuToggle
        ? hasSessions
          ? 'calc(100vh - 30px)'
          : '100vh'
        : hasSessions
        ? 'calc(100vh - 160px)'
        : 'calc(100vh - 130px)',
    [menuToggle, hasSessions]
  )

  const exportToPDF = async () => {
    if (!summaryRef.current) {
      toast({
        title: 'Resumo indisponível para exportação',
        description: 'Abra a aba Dashboard e aguarde o carregamento dos dados',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    setLoadingPDF(true)
    try {
      const currentDate = format(new Date(), 'dd-MM-yyyy')

      const group = await drawDOM(summaryRef.current, {
        scale: 0.5
      } as any)

      const dataUri = await exportPDF(group, {
        paperSize: 'A4',
        landscape: false,
        margin: '5mm'
      } as any)

      const link = document.createElement('a')
      link.href = dataUri
      link.download = `Dashboard-Cronograma-Semanal-${currentDate}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      toast({
        title: 'PDF exportado com sucesso!',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error) {
      toast({
        title: 'Erro ao exportar PDF',
        description: (error as Error)?.message || 'Erro desconhecido',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setLoadingPDF(false)
    }
  }

  useEffect(() => {
    getFilters({
      companies: true,
      centers: true,
      providersForPurchase: true,
      compactCategories: true,
      // Opções só para quem vê o filtro (Administrador/Analista de Planejamento)
      compradores: getVisualizaTodosCompradores()
    })
    getData()
    // Cancela a busca (provider global) ao sair da tela
    return () => cancel()
    // Carga inicial única: getData muda a cada alteração de filtro e
    // getFilters não é estável; incluí-los refaria a busca a cada mudança
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Histórico exige empresa: sem empresa volta para o Dashboard; com a aba
  // aberta, recarrega quando a seleção de empresas muda
  useEffect(() => {
    if (tabIndex !== HISTORICO_TAB) return
    if (!hasEmpresa) {
      setTabIndex(0)
      return
    }
    getHistorico(empresasIds)
    // empresasKey representa empresasIds de forma estável
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabIndex, empresasKey, hasEmpresa, getHistorico])

  return (
    <Layout title="Dashboard - Cronograma Semanal">
      <Grid
        w="100%"
        bg="white"
        h={height}
        templateColumns={!filters ? '100%' : '260px calc(100% - 260px)'}
      >
        {filters && (
          <LayoutFilters
            filters={filtersList}
            values={params}
            defaultDate={periodoFiltro}
            onChange={handleUpdateParams}
            onFilter={getData}
            onClearAllFilters={clearAllFilters}
            loading={loading}
            canCancel={false}
          />
        )}

        <Box bg="gray.50" h="100%" pt={4} px={4} overflow="auto">
          <Container maxW="full">
            <VStack spacing={6} align="stretch">
              {/* Header */}
              <Flex
                justify="space-between"
                align="center"
                bg="white"
                p={4}
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
                shadow="sm"
              >
                <VStack align="start" spacing={1}>
                  <Text fontSize="2xl" fontWeight="bold" color="gray.700">
                    Dashboard - Cronograma Semanal
                  </Text>
                  <Text fontSize="md" color="gray.600">
                    Monitoramento de compras e status de fornecedores
                  </Text>
                </VStack>

                <HStack spacing={3}>
                  <Button
                    onClick={() => setFilters(!filters)}
                    colorScheme={filters ? 'red' : 'blue'}
                    variant="outline"
                  >
                    {filters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                  </Button>
                  <Button
                    leftIcon={<FiRefreshCw />}
                    onClick={getData}
                    isLoading={loading}
                    loadingText="Atualizando..."
                    colorScheme="blue"
                    variant="outline"
                  >
                    Atualizar
                  </Button>
                  <Button
                    leftIcon={<FiFileText />}
                    onClick={exportToPDF}
                    colorScheme="purple"
                    variant="outline"
                    isDisabled={
                      statusCounts.total === 0 || loading || tabIndex !== 0
                    }
                    isLoading={loadingPDF}
                    loadingText="Gerando PDF..."
                    title="Exporta o resumo (gráficos e insights)"
                  >
                    Exportar PDF
                  </Button>
                  <Button
                    leftIcon={<FiDownload />}
                    onClick={exportData}
                    colorScheme="green"
                    isDisabled={statusCounts.total === 0 || loading}
                  >
                    Exportar CSV
                  </Button>
                </HStack>
              </Flex>

              {loadWarning && !loading && (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">{loadWarning}</Text>
                </Alert>
              )}

              <Tabs
                variant="soft-rounded"
                colorScheme="blue"
                index={tabIndex}
                onChange={setTabIndex}
                isLazy
              >
                <HStack mb={4} spacing={4}>
                  <TabList>
                    <Tab>Dashboard</Tab>
                    <Tab isDisabled={!hasEmpresa}>Histórico</Tab>
                  </TabList>
                  {!hasEmpresa && (
                    <Text fontSize="sm" color="gray.500">
                      Selecione ao menos uma empresa no filtro para ver o
                      histórico
                    </Text>
                  )}
                </HStack>
                <TabPanels>
                  <TabPanel p={0}>
                    {loading ? (
                      <LayoutLoader title={''} />
                    ) : (
                      <VStack spacing={6} align="stretch">
                        <VStack spacing={6} align="stretch" ref={summaryRef}>
                          {/* Gráficos de Status */}
                          <StatusCharts />

                          {/* Seção de Insights */}
                          <InsightsSection />
                        </VStack>

                        {/* Calendário de Compras */}
                        <CalendarView />

                        {/* Tabela de Dados */}
                        <DataTable />
                      </VStack>
                    )}
                  </TabPanel>
                  <TabPanel p={0} pt={4}>
                    <HistoricoTable
                      data={historico}
                      loading={loadingHistorico}
                    />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </VStack>
          </Container>
        </Box>
      </Grid>
    </Layout>
  )
}

export default PurchaseScheduleDashboard
